import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import PropTypes from "prop-types";
import configs from "../configs.js";

import GlobalStyle from "./GlobalStyle.js";
import Error from "./Error.js";

import { ApiContextProvider } from "./contexts/ApiContext.js";
import { AuthContextProvider } from "./contexts/AuthContext.js";
import { Telemetry } from "../telemetry.js";
import { CookieConsentModalContainer } from "./cookies/CookieConsentModalContainer.js";

import { Column } from "./layout/Flex.js";

import InitialLoading from "./InitialLoading.js";
import { ProjectElementsProvider } from "./contexts/ProjectsContext.js";
import { NotificationProvider } from "./contexts/NotificationContext.js";
import { SidebarContextProvider } from "./contexts/SidebarContext.js";
const EditorContainer = lazy(() =>
  import(/* webpackChunkName: "project-page", webpackPrefetch: true */ "./EditorContainer.js")
);
import { DialogContextProvider } from "./contexts/FluentDialogContext.js";
import { VersionControlProvider } from "./contexts/VersionControlContext.js";
import XrhubCatchAllRedirect from "./router/XrhubCatchAllRedirect.js";
import DashboardBridgeCallback from "./auth/DashboardBridgeCallback.js";
import {
  getEditorProjectKey,
  isLocalAuthCallbackRoute,
  isReservedProjectRoute,
  resolveLocalAuthCallbackPath
} from "./router/appRouteUtils.js";

const EditorPage = props => (
  <VersionControlProvider>
    <EditorContainer {...props} />
  </VersionControlProvider>
);

const LocalAuthCallbackBootstrap = ({ api }) => (
  <AuthContextProvider api={api}>
    <InitialLoading fullScreen api={api} />
  </AuthContextProvider>
);

export const AppShell = ({ api, isAuthenticated, setIsAuthenticated, userPermissions, routeProps }) => {
  const projectId = getEditorProjectKey(routeProps.match?.params);
  const requiresEditorPermission = projectId === "new" || projectId === "tutorial";
  const hasLegacyEditorPermission = userPermissions.some(
    permission => permission.toLowerCase() === configs.CREATOR_PERMISSION.toLowerCase()
  );
  const hasEditorPermission =
    !requiresEditorPermission || api.hasPermission(configs.CREATOR_PERMISSION) || hasLegacyEditorPermission;

  return (
    <SidebarContextProvider>
      <AuthContextProvider isAuthenticated={isAuthenticated} api={api}>
        <ProjectElementsProvider isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} api={api}>
          <NotificationProvider>
            <DialogContextProvider api={api}>
              <GlobalStyle />
              <Column as={Suspense} fallback={<InitialLoading fullScreen api={api} />}>
                {hasEditorPermission ? <EditorPage {...routeProps} /> : <Error message="404 Not Found" />}
              </Column>
              <Telemetry />
              <CookieConsentModalContainer />
            </DialogContextProvider>
          </NotificationProvider>
        </ProjectElementsProvider>
      </AuthContextProvider>
    </SidebarContextProvider>
  );
};

AppShell.propTypes = {
  api: PropTypes.object.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  setIsAuthenticated: PropTypes.func.isRequired,
  userPermissions: PropTypes.arrayOf(PropTypes.string),
  routeProps: PropTypes.shape({
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object
  }).isRequired
};

const EditorApp = ({ api, routeProps }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(api.isAuthenticated());
  const [userPermissions, setUserPermissions] = useState(api.getUserPermissions());

  useEffect(() => {
    const handleAuthChange = authStatus => {
      setIsAuthenticated(authStatus);
      setUserPermissions(api.getUserPermissions());
    };

    api.addListener("authentication-changed", handleAuthChange);

    return () => {
      api.removeListener("authentication-changed", handleAuthChange);
    };
  }, [api]);

  return (
    <AppShell
      api={api}
      isAuthenticated={isAuthenticated}
      setIsAuthenticated={setIsAuthenticated}
      userPermissions={userPermissions}
      routeProps={routeProps}
    />
  );
};

EditorApp.propTypes = {
  api: PropTypes.object.isRequired,
  routeProps: PropTypes.shape({
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object
  }).isRequired
};

LocalAuthCallbackBootstrap.propTypes = {
  api: PropTypes.object.isRequired
};

const App = ({ api }) => {
  const localAuthCallbackPath = resolveLocalAuthCallbackPath(configs.AUTH0_REDIRECT, configs.ROUTER_BASE_PATH);

  return (
    <ApiContextProvider value={api}>
      <Router basename={configs.ROUTER_BASE_PATH}>
        <Switch>
          {configs.isLocal && localAuthCallbackPath !== "/" && (
            <Route exact path={localAuthCallbackPath} render={() => <LocalAuthCallbackBootstrap api={api} />} />
          )}
          <Route
            exact
            path="/auth/bridge/callback"
            render={routeProps => <DashboardBridgeCallback api={api} routeProps={routeProps} />}
          />
          <Route
            exact
            path="/"
            render={routeProps => {
              if (
                configs.isLocal &&
                isLocalAuthCallbackRoute(
                  routeProps.location.pathname,
                  routeProps.location.search,
                  configs.AUTH0_REDIRECT,
                  configs.ROUTER_BASE_PATH
                )
              ) {
                return <LocalAuthCallbackBootstrap api={api} />;
              }

              return (
                <XrhubCatchAllRedirect
                  location={routeProps.location}
                  xrhubUrl={configs.XRHUB_URL}
                  creatorBasePath={configs.ROUTER_BASE_PATH}
                />
              );
            }}
          />
          <Route
            path="/projects/:workspaceId/:projectKey"
            render={routeProps => {
              if (isReservedProjectRoute(routeProps.match.params.projectKey)) {
                return (
                  <XrhubCatchAllRedirect
                    location={routeProps.location}
                    xrhubUrl={configs.XRHUB_URL}
                    creatorBasePath={configs.ROUTER_BASE_PATH}
                  />
                );
              }

              return <EditorApp api={api} routeProps={routeProps} />;
            }}
          />
          <Route
            path="/projects/:projectId"
            render={routeProps => {
              if (isReservedProjectRoute(routeProps.match.params.projectId)) {
                return (
                  <XrhubCatchAllRedirect
                    location={routeProps.location}
                    xrhubUrl={configs.XRHUB_URL}
                    creatorBasePath={configs.ROUTER_BASE_PATH}
                  />
                );
              }

              return <EditorApp api={api} routeProps={routeProps} />;
            }}
          />
          <Route
            render={routeProps => (
              <XrhubCatchAllRedirect
                location={routeProps.location}
                xrhubUrl={configs.XRHUB_URL}
                creatorBasePath={configs.ROUTER_BASE_PATH}
              />
            )}
          />
        </Switch>
      </Router>
    </ApiContextProvider>
  );
};

App.propTypes = {
  api: PropTypes.object.isRequired
};

export default App;
